import { baseApi } from './base.api';
import { projectsService } from './projectsService';
import { API_VERSION, PATH_KNOWLEDGE_BASE_API, PATH_PROJECTS_API } from '../constants/api-routes';
import { getApiVersion } from 'src/utils/apiVersion';

export const projectFilesService = baseApi.injectEndpoints({
  endpoints: (build) => {
    return {
      getProjectFiles: build.query({
        query: ({ projectId, sync }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}`,
          params: { sync: sync },
        }),
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: files } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  draft.files = files;
                },
              ),
            );
          } catch (err) {
            console.error('Error get project files:', err);
          }
        },
        providesTags: ['ProjectFiles'],
      }),
      addProjectFile: build.mutation({
        query: ({ projectId, body }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}`,
          method: 'POST',
          body: body,
          formData: true,
        }),
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: file } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  draft.files = draft.files || [];
                  draft.files.push(file);
                },
              ),
            );
          } catch (err) {
            console.error('Error add project files:', err);
          }
        },
      }),
      addProjectDocument: build.mutation({
        query: ({ projectId, body }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.ADD_DOCUMENT}`,
          method: 'POST',
          body: body,
        }),
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: file } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  draft.files = draft.files || [];
                  draft.files.push(file);
                },
              ),
            );
          } catch (err) {
            console.error('Error add project document:', err);
          }
        },
      }),
      updateProjectFiles: build.mutation({
        query: ({ projectId, body }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}`,
          method: 'PATCH',
          body: body,
        }),
        invalidatesTags: ['ProjectFiles'],
      }),
      deleteProjectFile: build.mutation({
        query: ({ projectId, fileId }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}/${fileId}`,
          method: 'DELETE',
        }),
        async onQueryStarted({ projectId, fileId }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            projectsService.util.updateQueryData(
              'getProjectById',
              { id: `${projectId}` },
              (draft) => {
                draft.files = draft.files.filter((file) => file.id !== fileId);
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch (err) {
            patchResult.undo();
            console.error('Error deleting project file:', err);
          }
        },
        invalidatesTags: ['ProjectFiles'],
      }),
      deleteProjectFiles: build.mutation({
        query: ({ projectId, body }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}`,
          method: 'DELETE',
          body: body,
        }),
        async onQueryStarted({ projectId, body }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            projectsService.util.updateQueryData(
              'getProjectById',
              { id: `${projectId}` },
              (draft) => {
                draft.files = draft.files.filter((file) => !body.includes(file.id));
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch (err) {
            patchResult.undo();
            console.error('Error deleting project file:', err);
          }
        },
      }),
      assignProjectKnowledgeBase: build.mutation({
        query: ({ projectId, project_type, body }) => {
          const version = getApiVersion();
          const path =
            version === API_VERSION.V1
              ? PATH_KNOWLEDGE_BASE_API.ASSIGN
              : PATH_KNOWLEDGE_BASE_API.ASSIGN_V2;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${path}`,
            method: 'POST',
            params: {
              project_type,
            },
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBase'],
      }),
      unassignProjectKnowledgeBase: build.mutation({
        query: ({ projectId, project_type, body }) => {
          const version = getApiVersion();
          const path =
            version === API_VERSION.V1
              ? PATH_KNOWLEDGE_BASE_API.UNASSIGN
              : PATH_KNOWLEDGE_BASE_API.UNASSIGN_V2;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${path}`,
            method: 'POST',
            params: {
              project_type,
            },
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBase'],
      }),
    };
  },
});

export const {
  useGetProjectFilesQuery,
  useAddProjectFileMutation,
  useAddProjectDocumentMutation,
  useUpdateProjectFilesMutation,
  useDeleteProjectFilesMutation,
  useDeleteProjectFileMutation,
  useAssignProjectKnowledgeBaseMutation,
  useUnassignProjectKnowledgeBaseMutation,
} = projectFilesService;
